import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Chip,
  IconButton,
  Typography,
  Divider,
  Paper,
  Button,
  useTheme,
  Skeleton,
  Select,
  MenuItem,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { observer } from "mobx-react-lite";
import alertConfigStore from "../../stores/AlertConfigStore";
import { DataGridPro } from "@mui/x-data-grid-pro";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import StraightenIcon from "@mui/icons-material/Straighten";
import BugReportIcon from "@mui/icons-material/BugReport";

const AlertConfigDetail = observer(() => {
  const location = useLocation();
  // const { deviceId } = useParams();
  const navigate = useNavigate();
  const { deviceRecord } = location.state || {};
  console.log("Device Record: ", deviceRecord);
  const theme = useTheme();
  // const normalizedId = String(deviceId).trim();

  // const deviceName = deviceStore.getDeviceName(normalizedId);
  const alertConfigId = deviceRecord?.alert_config_id || null;
  console.log("Alert config id: ", alertConfigId);
  const deviceName = deviceRecord?.name || "Unknown Device";
  const deviceId = deviceRecord?.pimios_id || useParams();
  const normalizedId = String(deviceId).trim();

  const config = alertConfigStore.getAlertConfigByObjectId(alertConfigId);
  const loading = alertConfigStore.loading;
  const error = alertConfigStore.error;

  const [activeTab, setActiveTab] = useState("Summary");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const tabList = ["Summary", "Raw Config", "Some Tab"];

  useEffect(() => {
    if (!alertConfigId) return;

    if (!alertConfigStore.getAlertConfigByObjectId(alertConfigId)) {
      alertConfigStore.fetchAlertConfigById(alertConfigId);
    }
  }, [alertConfigId]);

  const buildPageNumbers = useCallback((total, current) => {
    if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
    const range = [1];
    if (current > 3) range.push("...");
    for (
      let i = Math.max(2, current - 1);
      i <= Math.min(total - 1, current + 1);
      i++
    ) {
      range.push(i);
    }
    if (current < total - 2) range.push("...");
    range.push(total);
    return range;
  }, []);

  const flattenFaultCode = useCallback((obj, prefix = "") => {
    const result = [];
    for (const [k, v] of Object.entries(obj)) {
      if (v?.type === "fault-code") {
        result.push({ key: `${prefix}${k}`, ...v });
      } else if (typeof v === "object") {
        result.push(...flattenFaultCode(v, `${prefix}${k}.`));
      }
    }
    return result;
  }, []);

  const pageNumbers = useMemo(() => {
    if (!config) return [];
    const alertsConfig = config.alertsConfig ?? {};
    const oneBound = Object.values(alertsConfig).filter(
      (v) => v?.type === "one-bound"
    );
    const twoBound = Object.values(alertsConfig).filter(
      (v) => v?.type === "two-bound"
    );
    const faultCodes = flattenFaultCode(alertsConfig);
    const total = Math.max(
      Math.ceil(
        (oneBound.length + twoBound.length + faultCodes.length) / pageSize
      ),
      1
    );
    const current = Math.min(page, total);
    return buildPageNumbers(total, current);
  }, [config, pageSize, page, buildPageNumbers, flattenFaultCode]);

  if (!deviceRecord) {
    return (
      <Box p={3}>
        <Typography color="error">Missing device data.</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box p={3}>
        <Box className="flex mb-2 gap-x-2" sx={{ alignItems: "center" }}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6">
            Alert Configuration for Device: {deviceName}
          </Typography>
        </Box>
        <Skeleton
          variant="rectangular"
          height={60}
          sx={{ my: 2, borderRadius: 2 }}
        />
        <Skeleton
          variant="rectangular"
          height={160}
          sx={{ mb: 2, borderRadius: 2 }}
        />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
        <Button
          onClick={() => alertConfigStore.fetchAlertConfigById(alertConfigId)}
        >
          Retry
        </Button>
      </Box>
    );
  }

  if (!deviceId || alertConfigId === null) {
    return (
      <Box p={3}>
        <Typography>No alert configuration found for this device.</Typography>
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={() =>
            navigate(`/alerts/config/${normalizedId}/edit`, {
              state: { deviceRecord: deviceRecord },
            })
          }
        >
          Create New Alert Config
        </Button>
      </Box>
    );
  }

  // if (!config) return null;
  if (!config && alertConfigId) {
    return (
      <Box p={3}>
        <Typography color="textSecondary">
          No alert configuration data was found.
        </Typography>
      </Box>
    );
  }

  const { recipients, alertsConfig } = config;

  const oneBoundAlerts = Object.entries(alertsConfig)
    .filter(([, value]) => value?.type === "one-bound")
    .map(([key, value]) => ({ key, ...value }));

  const twoBoundAlerts = Object.entries(alertsConfig)
    .filter(([, value]) => value?.type === "two-bound")
    .map(([key, value]) => ({ key, ...value }));

  const faultCodeAlerts = flattenFaultCode(alertsConfig);

  const allAlerts = [
    ...oneBoundAlerts.map((a) => ({ ...a, _category: "one-bound" })),
    ...twoBoundAlerts.map((a) => ({ ...a, _category: "two-bound" })),
    ...faultCodeAlerts.map((a) => ({ ...a, _category: "fault-code" })),
  ];

  const totalPages = Math.max(Math.ceil(allAlerts.length / pageSize), 1);
  const currentPage = Math.min(page, totalPages);
  const paginatedRows = allAlerts
    .map((row, i) => ({ id: i, ...row }))
    .slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const formatMetricKey = (key) => {
    if (typeof key !== "string") return "";
    return key
      .split(".")
      .map((segment) => {
        if (segment === "v1") return "Valve 1";
        if (segment === "v2") return "Valve 2";
        return segment
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase())
          .trim();
      })
      .join(" › ");
  };

  const columns = [
    {
      field: "key",
      headerName: "Metric",
      flex: 1,
      renderHeader: (params) => <strong>{params.colDef.headerName}</strong>,
      renderCell: ({ value }) => formatMetricKey(value),
    },
    {
      field: "_category",
      headerName: "Type",
      width: 160,
      renderHeader: (params) => <strong>{params.colDef.headerName}</strong>,
      renderCell: (params) => {
        const type = params.value;
        const colorMap = {
          "one-bound": "warning",
          "two-bound": "info",
          "fault-code": "error",
        };
        return (
          <Chip
            label={type}
            color={colorMap[type]}
            variant="outlined"
            sx={{ textTransform: "capitalize" }}
          />
        );
      },
    },
    {
      field: "triggerMessage",
      headerName: "Trigger Message",
      flex: 2,
      renderHeader: (params) => <strong>{params.colDef.headerName}</strong>,
    },
    {
      field: "threshold",
      headerName: "Threshold",
      width: 120,
      renderHeader: (params) => <strong>{params.colDef.headerName}</strong>,
    },
    {
      field: "boundary",
      headerName: "Boundary",
      width: 100,
      renderHeader: (params) => <strong>{params.colDef.headerName}</strong>,
    },
    {
      field: "low",
      headerName: "Low",
      width: 100,
      renderHeader: (params) => <strong>{params.colDef.headerName}</strong>,
    },
    {
      field: "high",
      headerName: "High",
      width: 100,
      renderHeader: (params) => <strong>{params.colDef.headerName}</strong>,
    },
    {
      field: "faultCode",
      headerName: "Fault Code",
      width: 180,
      renderHeader: (params) => <strong>{params.colDef.headerName}</strong>,
    },
  ];

  return (
    <>
      <Box className="flex mb-2 gap-x-2" sx={{ alignItems: "center" }}>
        <IconButton onClick={() => navigate("/alerts")}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6">
          Alert Configuration for {deviceName}
        </Typography>
      </Box>

      <Box className="flex gap-2 mb-3">
        {tabList.map((tab) => (
          <Box
            key={tab}
            sx={{
              borderBottom: "2px solid",
              borderColor:
                activeTab === tab
                  ? theme.palette.mode === "dark"
                    ? theme.palette.primary.main
                    : "#22C55E"
                  : "transparent",
            }}
          >
            <Button
              onClick={() => setActiveTab(tab)}
              sx={{
                textTransform: "none",
                color:
                  activeTab === tab
                    ? theme.palette.mode === "dark"
                      ? theme.palette.primary.main
                      : "#000000"
                    : theme.palette.text.primary,
              }}
            >
              {tab}
            </Button>
          </Box>
        ))}
      </Box>

      {activeTab === "Summary" && (
        <>
          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Recipients
            </Typography>
            <Divider sx={{ mb: 1 }} />
            {/* <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {recipients.map((email) => (
                <Chip
                  key={email}
                  label={email}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box> */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {recipients?.email?.length > 0 && (
                <Box>
                  <Typography variant="subtitle2">Email Recipients</Typography>
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5 }}
                  >
                    {recipients.email.map((addr) => (
                      <Chip
                        key={addr}
                        label={addr}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}
              {recipients?.sms?.length > 0 && (
                <Box>
                  <Typography variant="subtitle2">SMS Recipients</Typography>
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5 }}
                  >
                    {recipients.sms.map((addr) => (
                      <Chip
                        key={addr}
                        label={addr}
                        color="secondary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>

          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
            {/* Summary Cards */}
            {/* One-Bound */}
            <Box
              sx={{
                backgroundColor: "#fff3e0",
                borderLeft: "6px solid #ff9800",
                px: 2,
                py: 1.5,
                borderRadius: 1,
                flex: "1 1 260px",
                display: "flex",
                alignItems: "center",
                boxShadow: 2,
              }}
            >
              <WarningAmberIcon sx={{ mr: 1, color: "#ff9800" }} />
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color="warning.dark"
                >
                  One-Bound Alerts
                </Typography>
                <Typography variant="body2">
                  {oneBoundAlerts.length} configured threshold alert
                  {oneBoundAlerts.length !== 1 ? "s" : ""}
                </Typography>
              </Box>
            </Box>

            {/* Two-Bound */}
            <Box
              sx={{
                backgroundColor: "#e3f2fd",
                borderLeft: "6px solid #2196f3",
                px: 2,
                py: 1.5,
                borderRadius: 1,
                flex: "1 1 260px",
                display: "flex",
                alignItems: "center",
                boxShadow: 2,
              }}
            >
              <StraightenIcon sx={{ mr: 1, color: "#2196f3" }} />
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color="info.dark"
                >
                  Two-Bound Alerts
                </Typography>
                <Typography variant="body2">
                  {twoBoundAlerts.length} configured range alert
                  {twoBoundAlerts.length !== 1 ? "s" : ""}
                </Typography>
              </Box>
            </Box>

            {/* Fault Code */}
            <Box
              sx={{
                backgroundColor: "#ffebee",
                borderLeft: "6px solid #f44336",
                px: 2,
                py: 1.5,
                borderRadius: 1,
                flex: "1 1 260px",
                display: "flex",
                alignItems: "center",
                boxShadow: 2,
              }}
            >
              <BugReportIcon sx={{ mr: 1, color: "#f44336" }} />
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color="error.dark"
                >
                  Fault-Code Alerts
                </Typography>
                <Typography variant="body2">
                  {faultCodeAlerts.length} fault-code alert
                  {faultCodeAlerts.length !== 1 ? "s" : ""}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              className="!normal-case"
              onClick={() =>
                navigate(`/alerts/config/${normalizedId}/edit`, {
                  state: { deviceRecord: deviceRecord, mode: "device" },
                })
              }
            >
              Edit Existing Alert Config
            </Button>
          </Box>

          <Box
            sx={{
              height: 600,
              width: "100%",
              boxShadow: 3,
              borderRadius: 1,
              mt: 3,
            }}
          >
            <DataGridPro
              rows={paginatedRows}
              columns={columns}
              density="comfortable"
              disableColumnMenu
              disableRowSelectionOnClick
              isCellEditable={() => false}
              hideFooter
              sx={{
                "& .MuiDataGrid-columnHeaders": {
                  fontWeight: "bold",
                },
                "& .MuiDataGrid-cell:focus": {
                  outline: "none",
                },
                "& .MuiDataGrid-cell": {
                  cursor: "default",
                },
              }}
            />
          </Box>

          <Box
            sx={{
              mt: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
            }}
          >
            <Button
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
            >
              Prev
            </Button>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              {pageNumbers.map((p, i) => (
                <Button
                  key={i}
                  onClick={() => setPage(p)}
                  variant={p === currentPage ? "contained" : "text"}
                  disabled={p === "..."}
                >
                  {p}
                </Button>
              ))}
            </Box>
            <Button
              onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography>Rows per page:</Typography>
              <Select
                size="small"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {[10, 25, 50, 100].map((n) => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>
        </>
      )}

      {activeTab === "Raw Config" && (
        <Paper elevation={2} sx={{ p: 2, mt: 3 }}>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {JSON.stringify(alertsConfig, null, 2)}
          </Typography>
        </Paper>
      )}

      {activeTab === "Some Tab" && (
        <Paper elevation={2} sx={{ p: 2, mt: 3 }}>
          <Typography variant="body1">Some Tab Content</Typography>
        </Paper>
      )}
    </>
  );
});

export default AlertConfigDetail;
